import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import useHankoAuth from '../hooks/useHankoAuth';
import {
    archiveAllNotifications,
    archiveNotifications,
    fetchNotifications,
    fetchUnreadNotificationCount,
    markAllNotificationsRead,
    markNotificationsRead,
    openNotificationStream,
} from '../api/notificationsApi';
import { getDemoNotifications, isNotificationDemoMode } from '../components/notifications/notificationDemo';
import { getNotificationPageInfo, loadNotificationsThroughUnread } from './notificationPaging';

const NotificationContext = createContext(null);

const readStreamEvents = async (response, onEvent, signal) => {
    const reader = response.body?.getReader();
    if (!reader) return;
    const decoder = new TextDecoder();
    let buffer = '';

    while (!signal?.aborted) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split(/\n\n+/);
        buffer = parts.pop() || '';
        parts.forEach(part => {
            const payload = part
                .split('\n')
                .map(line => line.startsWith('data:') ? line.slice(5).trim() : line.trim())
                .filter(Boolean)
                .join('');
            if (!payload) return;
            try {
                const event = JSON.parse(payload);
                if (event.type !== 'keepalive') onEvent(event);
            } catch (err) {
                // Ignore malformed keepalive/comment frames.
            }
        });
    }
};

export const NotificationProvider = ({ children }) => {
    const { user } = useHankoAuth();
    const [unreadCount, setUnreadCount] = useState(0);
    const [items, setItems] = useState([]);
    const [panelOpen, setPanelOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);
    const panelOpenRef = useRef(false);

    // DEV-ONLY demo mode (?notifDemo=1): serve local example items instead of
    // the BFF, and apply read/clear actions to them locally. See notificationDemo.js.
    const demoMode = isNotificationDemoMode();
    const demoItemsRef = useRef(null);
    const getDemoItems = useCallback(() => {
        if (!demoItemsRef.current) demoItemsRef.current = getDemoNotifications();
        return demoItemsRef.current;
    }, []);

    useEffect(() => {
        panelOpenRef.current = panelOpen;
    }, [panelOpen]);

    const refreshCount = useCallback(async () => {
        if (demoMode) {
            const count = getDemoItems().filter(item => !item.readAt).length;
            setUnreadCount(count);
            return count;
        }
        if (!user) return 0;
        const count = await fetchUnreadNotificationCount();
        setUnreadCount(count);
        return count;
    }, [demoMode, getDemoItems, user]);

    const loadPage = useCallback(async (nextPage = 0, append = false) => {
        if (demoMode) {
            setItems([...getDemoItems()]);
            setHasMore(false);
            setPage(0);
            return;
        }
        if (!user) return;
        setLoading(true);
        try {
            const data = await fetchNotifications(nextPage, 20);
            const content = data?.content || [];
            const pageInfo = getNotificationPageInfo(data, nextPage);
            setItems(prev => append ? [...prev, ...content] : content);
            setHasMore(pageInfo.hasMore);
            setPage(pageInfo.page);
        } finally {
            setLoading(false);
        }
    }, [demoMode, getDemoItems, user]);

    const loadInitialPages = useCallback(async (unreadTarget = 0) => {
        if (demoMode) {
            await loadPage(0, false);
            return;
        }
        if (!user) return;
        setLoading(true);
        try {
            const result = await loadNotificationsThroughUnread({
                fetchPage: fetchNotifications,
                unreadCount: unreadTarget,
            });
            setItems(result.items);
            setHasMore(result.hasMore);
            setPage(result.page);
        } finally {
            setLoading(false);
        }
    }, [demoMode, loadPage, user]);

    const refreshOpenPanel = useCallback(async () => {
        const count = await refreshCount();
        if (panelOpenRef.current) {
            await loadInitialPages(count);
        }
    }, [loadInitialPages, refreshCount]);

    useEffect(() => {
        if (!demoMode && !user) return;
        refreshCount().catch(err => console.warn('Failed to load notification count', err));
    }, [demoMode, refreshCount, user]);

    useEffect(() => {
        if (demoMode) return undefined;
        if (!user) return undefined;
        let stopped = false;
        let retryMs = 1000;
        let controller = null;

        const connect = async () => {
            while (!stopped) {
                controller = new AbortController();
                try {
                    const response = await openNotificationStream({ signal: controller.signal });
                    retryMs = 1000;
                    await readStreamEvents(response, event => {
                        if (event.type === 'unreadCount') {
                            setUnreadCount(event.count || 0);
                        } else {
                            refreshOpenPanel().catch(err => console.warn('Failed to refresh notifications', err));
                        }
                    }, controller.signal);
                } catch (err) {
                    if (!stopped) {
                        await new Promise(resolve => setTimeout(resolve, retryMs));
                        retryMs = Math.min(retryMs * 2, 30000);
                    }
                }
            }
        };

        connect();
        const poll = window.setInterval(() => {
            refreshOpenPanel().catch(err => console.warn('Failed to poll notifications', err));
        }, 60000);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                refreshOpenPanel().catch(err => console.warn('Failed to refresh visible notifications', err));
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            stopped = true;
            controller?.abort();
            window.clearInterval(poll);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [demoMode, refreshOpenPanel, user]);

    const openPanel = useCallback(async () => {
        setPanelOpen(true);
        const count = await refreshCount();
        await loadInitialPages(count);
    }, [loadInitialPages, refreshCount]);

    const closePanel = useCallback(() => setPanelOpen(false), []);

    const markRead = useCallback(async (id) => {
        if (!id) return;
        if (demoMode) {
            demoItemsRef.current = getDemoItems().map(item => item.id === id ? { ...item, readAt: item.readAt || new Date().toISOString() } : item);
        } else {
            await markNotificationsRead([id]);
        }
        setItems(prev => prev.map(item => item.id === id ? { ...item, readAt: item.readAt || new Date().toISOString() } : item));
        await refreshCount();
    }, [demoMode, getDemoItems, refreshCount]);

    const markAllRead = useCallback(async () => {
        if (demoMode) {
            demoItemsRef.current = getDemoItems().map(item => ({ ...item, readAt: item.readAt || new Date().toISOString() }));
        } else {
            await markAllNotificationsRead();
        }
        setUnreadCount(0);
        setItems(prev => prev.map(item => ({ ...item, readAt: item.readAt || new Date().toISOString() })));
    }, [demoMode, getDemoItems]);


    const archiveItem = useCallback(async (id) => {
        if (!id) return;
        if (demoMode) {
            demoItemsRef.current = getDemoItems().filter(item => item.id !== id);
        } else {
            await archiveNotifications([id]);
        }
        setItems(prev => prev.filter(item => item.id !== id));
        await refreshCount();
    }, [demoMode, getDemoItems, refreshCount]);

    const archiveAll = useCallback(async () => {
        if (demoMode) {
            demoItemsRef.current = [];
        } else {
            await archiveAllNotifications();
        }
        setUnreadCount(0);
        setItems([]);
        setHasMore(false);
        setPage(0);
    }, [demoMode, getDemoItems]);
    const loadMore = useCallback(() => loadPage(page + 1, true), [loadPage, page]);

    return (
        <NotificationContext.Provider value={{
            unreadCount,
            items,
            loading,
            hasMore,
            panelOpen,
            openPanel,
            closePanel,
            markRead,
            markAllRead,
            archiveItem,
            archiveAll,
            loadMore,
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotificationsContext = () => useContext(NotificationContext);